import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField, Button, Alert
} from '@one-thd/sui-atomic-components';
import {
  useLazyDataModel, params, string, arrayOf, shape, customType
} from '@thd-nucleus/data-sources';
import { localizeToProject, triggerAnalytics } from './helpers';
import {
  DELIVERY_ZIPCODE_UPDATED
} from './analytics';

const SAVE_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export const DeliveryZipcodeForm = ({
  projectId,
  assortmentZipCode,
  assortmentZipId,
  preferredStore,
  onUpdate,
  onSubmitSuccess,
  onSaveSuccess,
  reportSaveErrors
}) => {
  const [assortmentZip, setAssortmentZip] = useState(assortmentZipCode);
  const [assortmentZipError, setAssortmentZipError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const [saveError, setSaveError] = useState('');

  const isValidZipCode = (zipCode) => {
    return zipCode?.length === 5 && /^\d+$/.test(zipCode);
  };

  const [
    fetchCityState, { data: cityAndStateData, error: cityAndStateError, loading }
  ] = useLazyDataModel('cityStateLookupByZipCode', {
    variables: {},
    context: { withAuth: true },
    fetchPolicy: 'cache-first',
  });

  const [updateProjectDetails] = useLazyDataModel('updateProject', {
    variables: {
      projectId
    },
    refetchQueries: [
      'projectDetails'
    ],
    context: { withAuth: true }
  });

  const handleOnChange = (__, event) => {
    const { value } = event.target;
    setAssortmentZip(value);
    setHasEdited(true);
    if (!isValidZipCode(value)) {
      setAssortmentZipError('Invalid Zip Code');
    }
  };

  // nucleus runs mutations with errorPolicy 'all', so a GraphQL error resolves with `errors`
  // while a network error rejects. Both count as a failed save. Without reportSaveErrors the
  // rejection is rethrown, so existing consumers stop exactly where they did before. [DSCVR-5186]
  const saveAssortmentZip = async () => {
    try {
      const result = await updateProjectDetails({
        variables: {
          operationType: 'ADDRESS',
          addressInput: {
            addressType: 'ASSORTMENT_ZIP',
            zipCode: assortmentZip,
            id: assortmentZipId
          }
        }
      });
      return !result?.errors?.length;
    } catch (err) {
      if (!reportSaveErrors) {
        throw err;
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!hasEdited) {
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      return;
    }
    if (assortmentZipError) {
      setShowErrors(true);
      return;
    }
    setSaveError('');
    const isSaved = await saveAssortmentZip();
    if (isSaved) {
      onSaveSuccess(assortmentZip);
    } else if (reportSaveErrors) {
      setSaveError(SAVE_ERROR_MESSAGE);
      return;
    }
    if (preferredStore) {
      localizeToProject({
        assortmentZip,
        preferredStore,
        analyticsString: DELIVERY_ZIPCODE_UPDATED,
        onUpdate: () => {
          onUpdate();
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        }
      });
    } else {
      triggerAnalytics(DELIVERY_ZIPCODE_UPDATED, {});
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    }
  };

  useEffect(() => {
    if (isValidZipCode(assortmentZip) && hasEdited) {
      fetchCityState({
        variables: {
          zipCode: assortmentZip
        }
      });
    }
  }, [assortmentZip]);

  // Update assortmentZip state when assortmentZipCode prop changes
  useEffect(() => {
    if (assortmentZipCode && !hasEdited) {
      setAssortmentZip(assortmentZipCode);
    }
  }, [assortmentZipCode, hasEdited]);

  useEffect(() => {
    if (!hasEdited) return;
    const cityAndStateArray = cityAndStateData?.cityStateLookupByZipCode?.postalDetailsList || [];
    if (cityAndStateArray.length && !loading && !cityAndStateError && isValidZipCode(assortmentZip)) {
      setAssortmentZipError('');
    } else {
      setAssortmentZipError('Invalid Zip Code');
    }
  }, [cityAndStateData, loading, cityAndStateError, assortmentZip, hasEdited]);

  return (
    <div className="sui-flex sui-flex-col sui-gap-3 sui-m-4">
      <TextField
        required
        id="project-zip"
        label="Delivery Zip"
        type="text"
        value={assortmentZip || ''}
        placeholder="Enter Zip Code"
        fullWidth
        onChange={(event) => { handleOnChange('assortmentZip', event); }}
        status={showErrors && assortmentZipError ? 'error' : null}
        statusMessage={showErrors ? assortmentZipError : ''}
        InputProps={{
          inputProps: {
            'data-testid': 'project-zip-input',
            maxLength: '5'
          }
        }}
      />
      {saveError && (
        <Alert status="error" data-testid="delivery-zip-save-error">{saveError}</Alert>
      )}
      <Button data-testid="delivery-zip-update-button" onClick={handleSubmit} fullWidth>Update</Button>
    </div>
  );
};

DeliveryZipcodeForm.displayName = 'DeliveryZipcodeForm';

DeliveryZipcodeForm.propTypes = {
  projectId: PropTypes.string,
  assortmentZipCode: PropTypes.string,
  assortmentZipId: PropTypes.string,
  preferredStore: PropTypes.string,
  onUpdate: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  /** Called with the zip once the save is confirmed - not when it fails. [DSCVR-5186] */
  onSaveSuccess: PropTypes.func,
  /**
   * When true, a failed save keeps the drawer open with an error instead of continuing as if it
   * had worked. Off by default so existing consumers keep their current behavior. [DSCVR-5186]
   */
  reportSaveErrors: PropTypes.bool
};

DeliveryZipcodeForm.defaultProps = {
  projectId: '',
  assortmentZipCode: '',
  assortmentZipId: '',
  preferredStore: '',
  onUpdate: () => {},
  onSubmitSuccess: null,
  onSaveSuccess: () => {},
  reportSaveErrors: false
};

DeliveryZipcodeForm.dataModel = {
  cityStateLookupByZipCode: params({
    zipCode: string().isRequired(),
  }).shape({
    postalDetailsList: arrayOf(
      shape({
        city: string(),
        state: string()
      })
    )
  }),
  updateProject: params({
    projectId: string().isRequired(),
    operationType: customType('OperationType').enum(['STATUS', 'INFO', 'ADDRESS']).isRequired(),
    addressInput: customType('AddressUpdate').shape({
      id: string().isRequired(),
      addressType: customType('AddressType').enum(['ASSORTMENT_ZIP', 'FULFILLMENT', 'JOB_SITE']).isRequired(),
      zipCode: string().isRequired(),
    })
  }).mutation().shape({
    id: string()
  })
};
